/**
 * The signed-in account's saved words, held for the session.
 *
 * Redux rather than the useSyncExternalStore-over-a-module-singleton pattern in
 * hooks/useUserSettings.ts, and that is a correctness requirement rather than a
 * style preference: that module keeps its snapshot in module scope, which under
 * SSR is shared by every request a workerd isolate handles. It gets away with
 * it because its state is never server-derived. A favorite set there would let
 * one account's saved words render into another account's HTML. makeStore() is
 * already a per-render factory for exactly this reason.
 *
 * The whole set lives here so the heart on a word page is a selector lookup
 * rather than a request — one /api/me call per session answers every word page
 * the user visits.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
   MeResponse,
   SavedFavorite,
   SavedLang,
   SavedTag,
} from '../../shared/favorites-contract'

/**
 * `${lang}:${word}`.
 *
 * Safe because the two languages are disjoint in practice: Thai headwords are
 * Thai script, and English ones come from eng_words. Should a headword ever
 * contain a colon the key would still be unique — 'eng:a:b' cannot collide with
 * 'thai:...' — since only the first colon is ever the separator.
 */
export type FavoriteKey = string

export const favoriteKey = (lang: SavedLang, word: string): FavoriteKey => `${lang}:${word}`

export type FavoritesState = {
   /**
    * 'idle' on the server and through hydration, and nothing else until an
    * effect has run. That is what keeps user state out of the SSR output and
    * the first client render byte-identical to it.
    */
   status: 'idle' | 'loading' | 'loaded' | 'error'
   byKey: Record<FavoriteKey, SavedFavorite>
   tags: Record<number, SavedTag>
   /** Keys with a write in flight; the heart drops clicks while set. */
   pending: Record<FavoriteKey, true>
   /** Whether this account may add tags. Removing them is never gated. */
   tagsEntitled: boolean
   /** Keys the dictionary no longer knows, found while hydrating the list. */
   dangling: Record<FavoriteKey, true>
}

const initialState: FavoritesState = {
   status: 'idle',
   byKey: {},
   tags: {},
   pending: {},
   tagsEntitled: false,
   dangling: {},
}

const indexTags = (tags: SavedTag[]): Record<number, SavedTag> =>
   Object.fromEntries(tags.map((tag) => [tag.id, tag]))

const favoritesSlice = createSlice({
   name: 'favorites',
   initialState,
   reducers: {
      loadStarted(state) {
         state.status = 'loading'
      },

      loadSucceeded(state, action: PayloadAction<MeResponse>) {
         state.status = 'loaded'
         state.byKey = Object.fromEntries(
            action.payload.favorites.map((favorite) => [
               favoriteKey(favorite.lang, favorite.word),
               favorite,
            ])
         )
         state.tags = indexTags(action.payload.tags)
         state.tagsEntitled = action.payload.entitlements.tags
         state.pending = {}
         state.dangling = {}
      },

      loadFailed(state) {
         state.status = 'error'
      },

      /**
       * Signing out, or switching accounts. Everything goes: leaving one
       * account's words behind for the next one is the worst bug this slice
       * could have.
       */
      cleared() {
         return { ...initialState }
      },

      writeStarted(state, action: PayloadAction<FavoriteKey>) {
         state.pending[action.payload] = true
      },

      optimisticAdded(state, action: PayloadAction<{ key: FavoriteKey; favorite: SavedFavorite }>) {
         state.byKey[action.payload.key] = action.payload.favorite
      },

      optimisticRemoved(state, action: PayloadAction<FavoriteKey>) {
         delete state.byKey[action.payload]
      },

      /**
       * Replaces the optimistic row wholesale, so the id and snapshot the
       * client guessed are never what the session goes on to use.
       */
      writeConfirmed(state, action: PayloadAction<{ key: FavoriteKey; favorite: SavedFavorite }>) {
         state.byKey[action.payload.key] = action.payload.favorite
         delete state.pending[action.payload.key]
         delete state.dangling[action.payload.key]
      },

      writeRemoved(state, action: PayloadAction<FavoriteKey>) {
         delete state.byKey[action.payload]
         delete state.pending[action.payload]
         delete state.dangling[action.payload]
      },

      /** `previous` is null when the row did not exist before the attempt. */
      writeRolledBack(
         state,
         action: PayloadAction<{ key: FavoriteKey; previous: SavedFavorite | null }>
      ) {
         if (action.payload.previous) state.byKey[action.payload.key] = action.payload.previous
         else delete state.byKey[action.payload.key]
         delete state.pending[action.payload.key]
      },

      /** The server's answer for one word: a replacement, never a merge. */
      tagsReplaced(
         state,
         action: PayloadAction<{ key: FavoriteKey; tagIds: number[]; tags: SavedTag[] }>
      ) {
         const favorite = state.byKey[action.payload.key]
         if (favorite) favorite.tagIds = action.payload.tagIds
         state.tags = indexTags(action.payload.tags)
      },

      tagUpserted(state, action: PayloadAction<SavedTag>) {
         state.tags[action.payload.id] = action.payload
      },

      tagRemoved(state, action: PayloadAction<{ id: number; mergedInto: number | null }>) {
         delete state.tags[action.payload.id]
         for (const favorite of Object.values(state.byKey)) {
            if (!favorite.tagIds.includes(action.payload.id)) continue
            const remaining = favorite.tagIds.filter((id) => id !== action.payload.id)
            // A merge moves the word onto the target unless it was already there.
            if (
               action.payload.mergedInto !== null &&
               !remaining.includes(action.payload.mergedInto)
            ) {
               remaining.push(action.payload.mergedInto)
            }
            favorite.tagIds = remaining
         }
      },

      markedDangling(state, action: PayloadAction<FavoriteKey[]>) {
         for (const key of action.payload) state.dangling[key] = true
      },

      /** Fresher dictionary data merged over a stored snapshot on the list page. */
      hydrated(state, action: PayloadAction<{ key: FavoriteKey; t2e: string; meaning: string }[]>) {
         for (const update of action.payload) {
            const favorite = state.byKey[update.key]
            if (!favorite) continue
            favorite.t2e = update.t2e
            favorite.meaning = update.meaning
            delete state.dangling[update.key]
         }
      },
   },
})

export const favoritesActions = favoritesSlice.actions
export default favoritesSlice.reducer
